import type { ApiClient } from "@api/ApiClient";
import { reportError } from "@utils/observability/reportError";
import type {
  AddCustomPromptResponse,
  CustomPromptGetResponse,
  DocumentToPromptResponse,
  PromptMetadata,
} from "./types";

interface AddCustomPromptApiParams {
  promptName: string;
  promptBody: string;
  favorite?: boolean;
  id?: string;
  promptMetadata?: PromptMetadata;
}

interface EditCustomPromptApiParams {
  promptId: string;
  promptName?: string;
  promptBody?: string;
  favorite?: boolean;
  override?: boolean;
  promptMetadata?: PromptMetadata;
}

interface DocumentToPromptApiParams {
  file: File;
  parseVariables: boolean;
}

export const getCustomPromptsApi = async (apiClient: ApiClient): Promise<CustomPromptGetResponse> => {
  const results = await apiClient.get("/custom_prompts");
  if (!results) {
    const error = new Error("No response from server");
    reportError("customPrompts.get", error);
    throw error;
  }
  return results.json() as Promise<CustomPromptGetResponse>;
};

export const addCustomPromptApi = async (
  apiClient: ApiClient,
  { promptName, promptBody, favorite = false, id = "", promptMetadata }: AddCustomPromptApiParams
): Promise<AddCustomPromptResponse> => {
  const body = JSON.stringify({
    promptBody,
    promptName,
    favorite,
    overrideId: id,
    promptMetadata,
  });
  const results = await apiClient.post("/custom_prompt", body, JSON_HEADERS);
  if (!results) {
    const error = new Error("No response from server");
    reportError("customPrompts.add", error);
    throw error;
  }
  return results.json() as Promise<AddCustomPromptResponse>;
};

export const deleteCustomPromptApi = async (apiClient: ApiClient, promptID: string): Promise<void> => {
  await apiClient.delete(`/custom_prompt?promptId=${promptID}`);
};

export const editCustomPromptApi = async (
  apiClient: ApiClient,
  {
    promptId,
    promptName = "",
    promptBody = "",
    favorite = false,
    override = false,
    promptMetadata,
  }: EditCustomPromptApiParams
): Promise<void> => {
  const body = JSON.stringify({
    promptId,
    promptName,
    promptBody,
    favorite,
    override,
    promptMetadata,
  });
  await apiClient.put("/custom_prompt", body, JSON_HEADERS);
};

export const documentToPromptApi = async (
  apiClient: ApiClient,
  { file, parseVariables }: DocumentToPromptApiParams
): Promise<DocumentToPromptResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("parseVariables", String(parseVariables));
  const results = await apiClient.post("/custom_prompt/document", formData);
  if (!results) {
    const error = new Error("No response from server");
    reportError("customPrompts.documentToPrompt", error);
    throw error;
  }
  return results.json() as Promise<DocumentToPromptResponse>;
};

const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };
